import type { Texture } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { MANIFEST } from '@/assets/manifest';
import { PROJECTILE_LOOK as L } from '@/config/projectile-look';
import type { Bullet } from './bullet-types';

/**
 * Hostile projectile animation (docs/04 danger language): every enemy
 * texture with a `look` pulses its warning glow; fused shots count their
 * fuse down to the split. Frame lists are built once per key, so a fight
 * allocates nothing here.
 */
const frameCache = new Map<string, ReadonlyArray<Texture> | null>();

/** Animation frames of an enemy texture — null when it has no `look`. */
export function lookFrames(key: string): ReadonlyArray<Texture> | null {
  let frames = frameCache.get(key);
  if (frames === undefined) {
    const def = MANIFEST.get(key);
    frames = null;
    if (def?.look) {
      const list: Texture[] = [];
      for (let i = 0; i < def.frames; i++) list.push(getTexture(key, i));
      frames = list;
    }
    frameCache.set(key, frames);
  }
  return frames;
}

/** Sprite size multiplier — fused textures leave room for the danger ring. */
export function lookScale(fused: boolean): number {
  return fused ? L.fuseSpriteScale : 1;
}

/** Current frame: a pulse for ordinary shots, the fuse step for bombs. */
export function lookFrame(b: Bullet, clock: number): Texture {
  const frames = b.frames!;
  if (b.fused) {
    const left = b.fuse > 0 ? b.age / b.fuse : 0;
    const step = Math.min(L.fuseSteps - 1, Math.floor(left * L.fuseSteps));
    const flick = Math.floor(clock * L.fuseFlickerFps) & 1;
    return frames[Math.min(frames.length - 1, step * 2 + flick)]!;
  }
  return frames[(Math.floor(clock * L.pulseFps) + b.phase) % frames.length]!;
}
